import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useBooleanFlagValue } from '@openfeature/react-sdk';
import { AgentsService } from '@inference.engine/api-client/web';
import { inferenceHttp } from '@/lib/api/inference-client';
import { ADMIN_QUERY_KEYS } from '@/features/admin/api/query-keys';
import { getCurrentOrganizationId } from '@/entities/user';
import { getAccessOverview, getAllOrgWorkspaces } from '@/features/admin/api';
import type { ToolRowData } from '@/features/admin/ui/ToolsTable';
import { MANAGED_TOOLS } from '@/shared/config/managedTools';

export function useToolsAccessData() {
  const organizationId = getCurrentOrganizationId();
  const isNACBIntakeEnabled = useBooleanFlagValue('nacb_trigger_intake', false);
  const isFabricEnabled = useBooleanFlagValue('fabric_tool', false);

  const agentsQuery = useQuery({
    queryKey: [...ADMIN_QUERY_KEYS.tools.agents, 'admin'],
    queryFn: async () => {
      const response = await AgentsService.getAllAgents({
        client: inferenceHttp,
        throwOnError: true,
        query: {
          includeNonVisibleAgents: false,
          showAll: true,
          includeHiddenCustomAgents: true,
        },
      });
      return response.data;
    },
  });

  const workspacesQuery = useQuery({
    queryKey: ADMIN_QUERY_KEYS.tools.orgWorkspaces,
    queryFn: getAllOrgWorkspaces,
  });

  const accessOverviewQuery = useQuery({
    queryKey: [...ADMIN_QUERY_KEYS.tools.accessOverview, organizationId],
    queryFn: () => getAccessOverview(organizationId),
    enabled: !!organizationId,
  });

  const workspaces = useMemo(
    () => [...(workspacesQuery.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [workspacesQuery.data],
  );

  const isLoading =
    agentsQuery.isLoading || workspacesQuery.isLoading || accessOverviewQuery.isLoading;

  const isError = agentsQuery.isError || workspacesQuery.isError || accessOverviewQuery.isError;

  const { refetch: refetchAgents } = agentsQuery;
  const { refetch: refetchWorkspaces } = workspacesQuery;
  const { refetch: refetchAccessOverview } = accessOverviewQuery;

  // Any one of the three failing leaves the table wrong, so a retry reloads
  // all three rather than only the query that reported the error.
  const refetch = useCallback(async () => {
    await Promise.all([refetchAgents(), refetchWorkspaces(), refetchAccessOverview()]);
  }, [refetchAgents, refetchWorkspaces, refetchAccessOverview]);

  const tools = useMemo<ToolRowData[]>(() => {
    const agents = agentsQuery.data ?? [];
    const overview = accessOverviewQuery.data ?? [];

    const overviewByAgentId = new Map(overview.map((item) => [item.agentId, item] as const));

    const result = agents.map<ToolRowData>((agent) => {
      const access = overviewByAgentId.get(agent.id);
      const mode = access?.mode ?? 'all';
      const affectedWorkspaces =
        mode !== 'all' ? workspaces.filter((ws) => access?.workspaceIds.includes(ws.id)) : [];

      const configuredWsIds = agent.configurationStatus?.fullyConfiguredWorkspaceIds ?? [];
      const fullyConfiguredWorkspaces = workspaces.filter((ws) => configuredWsIds.includes(ws.id));

      return {
        id: agent.id,
        name: agent.name,
        description: agent.description ?? '',
        tag: agent.tag,
        source: agent.source,
        workspaceAccess: mode,
        workspaces: affectedWorkspaces,
        fullyConfiguredWorkspaces,
        configurationStatus: agent.configurationStatus,
      };
    });

    // Content Studio and Reels Creator are listed unconditionally: this row is where the
    // per-workspace ACL gets configured, so gating it on the rollout flag would leave the ACL
    // unreachable until the flag is on. Fabric waits for its flag instead: its service is not
    // deployed everywhere yet, and a switch for a tool nobody can open only confuses.
    const visibleManagedTools = MANAGED_TOOLS.filter((managedTool) => {
      if (managedTool.id === 'nacb-intake') return isNACBIntakeEnabled;
      if (managedTool.id === 'fabric') return isFabricEnabled;
      return true;
    });

    const managedRows = visibleManagedTools.map<ToolRowData>((managedTool) => {
      const access = overviewByAgentId.get(managedTool.id);
      const mode = access?.mode ?? 'all';
      const affectedWorkspaces =
        mode !== 'all' ? workspaces.filter((ws) => access?.workspaceIds.includes(ws.id)) : [];

      return {
        id: managedTool.id,
        name: managedTool.name,
        description: managedTool.description,
        tag: managedTool.tag,
        source: 'system',
        workspaceAccess: mode,
        workspaces: affectedWorkspaces,
        fullyConfiguredWorkspaces: [],
      };
    });

    return [...result, ...managedRows];
  }, [
    agentsQuery.data,
    accessOverviewQuery.data,
    isNACBIntakeEnabled,
    isFabricEnabled,
    workspaces,
  ]);

  return {
    tools,
    workspaces,
    isLoading,
    isError,
    refetch,
  };
}
